import React, { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FormSpacer, InformationPopUp } from 'components';
import { FormDataType, productTypeList } from 'contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { noop } from 'lodash/fp';
import { Button, FormGroupLabel } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';

import { DropdownController, TextFieldController } from 'controllers';
import { SEPARATOR } from 'utils/constants';
import { TrackEvent, formatNext } from 'hooks/useAnalytics';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  form: UseFormReturn<FormDataType>;
  show: boolean;
  isValid: boolean;
  showContinueButton: boolean;
  onSubmit: () => void;
}

const BreachOtherDetails: FC<Props> = ({
  form,
  show = false,
  isValid = false,
  showContinueButton = true,
  onSubmit = noop,
}) => {
  const { control, watch, getValues } = form;

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const TEXT_CONTENT = {
    heading: getTextContent('breach.tell.us.more.otherDetails.heading'),
    product: getTextContent('breach.tell.us.more.otherDetails.product'),
    placeholder: getTextContent('breach.tell.us.more.otherDetails.placeholder'),
    policyNumber: getTextContent('breach.tell.us.more.otherDetails.policyNumber'),
    policyNumberInfo: getTextContent('breach.tell.us.more.otherDetails.policyNumber.infoIcon'),
    claimNumber: getTextContent('breach.tell.us.more.otherDetails.claimNumber'),
    claimNumberInfo: getTextContent('breach.tell.us.more.otherDetails.claimNumber.infoIcon'),
    customersAffected: getTextContent('breach.tell.us.more.otherDetails.customersAffected'),
    numberOfCustomer: getTextContent('breach.tell.us.more.otherDetails.numberOfcustomer'),
    numberOfCustomerPlaceholder: getTextContent('breach.tell.us.more.otherDetails.numberOfcustomer.placeholder'),
    financialLoss: getTextContent('breach.tell.us.more.otherDetails.financialLoss'),
    estimatedLoss: getTextContent('breach.tell.us.more.otherDetails.estimatedLoss'),
    estimatedLossPlaceholder: getTextContent('breach.tell.us.more.otherDetails.estimatedLoss.placeholder'),
    nextLabel: getTextContent('next.label'),
  };

  const isFinancialLoss = watch('financialLoss') === 'yes';
  const isCustomersAffected = watch('customersAffected') === 'yes';
  const isTMD = watch('breachType') === 'Dealings Outside of TMD'; // Breach type

  if (!show) return null;

  const handleClick = () => {
    TrackEvent(formatNext('nextResolutionDetails'), getValues());
    onSubmit();
  };

  return (
    <FormCard variant="sectionTitle" title={TEXT_CONTENT.heading}>
      <>
        <FormGroupLabel alignCenter title={TEXT_CONTENT.product} required />
        <FormSpacer type="input" />
        <DropdownController
          control={control}
          capture
          name="productType"
          label={TEXT_CONTENT.placeholder}
          optionList={productTypeList.map((value) => {
            return {
              label: value,
              value,
            };
          })}
        />

        <FormGroupLabel
          alignCenter
          title={TEXT_CONTENT.policyNumber}
          titleSuffix={
            <InformationPopUp message={TEXT_CONTENT.policyNumberInfo} dataTrack="infoIconBreachPolicyNumber" />
          }
        />
        <FormSpacer type="input" />
        <TextFieldController
          control={control}
          maxLength={50}
          name="policyNumber"
          label="Policy number"
          inputProps={{
            maxLength: 100,
            minLength: 4,
          }}
          data-track="breachPolicyNumber"
          capture
          data-test="breachPolicyNumber"
        />

        {!isTMD && (
          <>
            <FormGroupLabel
              alignCenter
              title={TEXT_CONTENT.claimNumber}
              titleSuffix={
                <InformationPopUp message={TEXT_CONTENT.claimNumberInfo} dataTrack="infoIconBreachClaimNumber" />
              }
            />
            <FormSpacer type="input" />
            <TextFieldController
              control={control}
              maxLength={50}
              label="Claim number"
              name="claimNumber"
              inputProps={{
                maxLength: 100,
                minLength: 4,
              }}
              capture
            />
          </>
        )}

        <Controller
          name="customersAffected"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Toggle
              {...field}
              id={field.name}
              required
              title={TEXT_CONTENT.customersAffected}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value;
                field.onChange(value);
              }}
              data-track="customersAffected"
              data-test="customersAffected"
              data-track-value="capture"
              optionList={[
                {
                  text: 'Yes',
                  value: 'yes',
                },
                {
                  text: 'No',
                  value: 'no',
                },
              ].map(({ text, value }) => ({
                checked: value === field.value,
                id: `${field.name}${SEPARATOR}${value}`,
                label: text,
                value,
              }))}
            />
          )}
        />

        {isCustomersAffected && (
          <>
            <FormGroupLabel alignCenter required title={TEXT_CONTENT.numberOfCustomer} />
            <FormSpacer type="input" />
            <TextFieldController
              name="customerNumber"
              control={control}
              label={TEXT_CONTENT.numberOfCustomerPlaceholder}
              inputProps={{
                maxLength: 10,
                minLength: 1,
              }}
              capture
            />

            <Controller
              name="financialLoss"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Toggle
                  {...field}
                  id={field.name}
                  required
                  title={TEXT_CONTENT.financialLoss}
                  onChange={(e) => {
                    const value = (e.target as HTMLInputElement).value;
                    field.onChange(value);
                  }}
                  data-track="financialLoss"
                  data-track-value="capture"
                  optionList={[
                    {
                      text: 'Yes',
                      value: 'yes',
                    },
                    {
                      text: 'No',
                      value: 'no',
                    },
                  ].map(({ text, value }) => ({
                    checked: value === field.value,
                    id: `${field.name}${SEPARATOR}${value}`,
                    label: text,
                    value,
                  }))}
                />
              )}
            />
          </>
        )}

        {isFinancialLoss && (
          <>
            <FormGroupLabel alignCenter required title={TEXT_CONTENT.estimatedLoss} />
            <FormSpacer type="input" />
            <TextFieldController
              name="estimatedLoss"
              control={control}
              label={TEXT_CONTENT.estimatedLossPlaceholder}
              maxLength={15}
              capture
            />
          </>
        )}
      </>

      {showContinueButton && (
        <>
          <FormSpacer type="formgroup" />
          <Button type="button" label={TEXT_CONTENT.nextLabel} disabled={!isValid} onClick={handleClick} />
        </>
      )}
    </FormCard>
  );
};

export default BreachOtherDetails;
